import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../../ui';
import AsyncCustomSelect from '../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../ui/custom_react_select/custom_select';
import { PencilIcon, TrashIcon } from '../../../../../ui/icons/icons';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { Assessment } from '../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { usePreScreenQuestions } from './Hooks/UsePreScreenQuestions';

interface IPreScreenQuestions {
  assessmentInfo: Assessment;
  setAssessmentInfo: Dispatch<SetStateAction<Assessment | undefined>>;
}
const questionType: SelectOptions[] = [
  {
    label: 'Numeric',
    value: 'numeric',
  },
  {
    label: 'Multiple Choice',
    value: 'multiple-choice',
  },
  {
    label: 'Video',
    value: 'video',
  },
];
export const PreScreenQuestions = (props: IPreScreenQuestions) => {
  const { assessmentInfo, setAssessmentInfo } = props;
  const { t } = useTranslation(NS.adminPages);
  const AA = ADMIN_PAGES_KEYS.jobOpenings.autoAssessTab;
  const {
    choices,
    defaultOptions,
    editChoices,
    setNewQuestionType,
    handleSaveQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleQuestionChange,
    handleChoiceChange,
    handleAddQuestion,
    loadOptionsDebounced,
    setNewQuestionText,
    setEditChoices,
    editQuestionType,
    setEditQuestionType,
    editQuestionText,
    setEditQuestionText,
    editIndex,
    questions,
    newQuestionText,
    newQuestionType,
    setChoices,
    setEditIndex,
  } = usePreScreenQuestions({ assessmentInfo, setAssessmentInfo });
  return (
    <div className='flex flex-col p-3 sm:p-5 relative gap-4 sm:gap-6 bg-white rounded-xl shadow-sm border border-slate-200/60'>
      { /* ─── Entry Section ─── */ }
      <div className='flex flex-col gap-4 sm:gap-5 p-3 sm:p-4 bg-slate-50/40 rounded-xl border border-slate-100'>
        <div className='flex items-center gap-2 mb-1'>
          <div className='w-1 h-5 bg-emerald-500 rounded-full' />
          <h3 className='text-sm font-bold text-slate-800 uppercase tracking-tight'>
            { t(AA.addQuestions) }
          </h3>
        </div>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-3 items-end'>
          { /* Bank Search - 5 cols */ }
          <div className='lg:col-span-5'>
            <AsyncCustomSelect
                name='preScreenQuestion'
                value={[]}
                placeholder={t(AA.searchFromBank)}
                defaultOptions={defaultOptions}
                onChange={handleQuestionChange}
                loadOptions={(inputValue, callback) =>
                  loadOptionsDebounced(inputValue, callback)
                }
                label={t(AA.importFromBank)}
            />
          </div>

          { /* Divider For Desktop */ }
          <div className='hidden lg:flex flex-col items-center justify-center h-9 px-2'>
            <div className='w-px h-full bg-slate-200' />
          </div>

          { /* Manual Entry - 6 cols */ }
          <div className='lg:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4'>
            <TextInput
                label={t(AA.manualQuestion)}
                name='question'
                value={newQuestionText}
                type='text'
                placeholder={t(AA.typeQuestion)}
                onChange={(e) => setNewQuestionText(e.target.value)}
                disabled={editIndex !== null}
            />
            <CustomSelect
                label={t(AA.questionType)}
                name='questionType'
                value={questionType.find(
                  (option) => option.value === newQuestionType,
                )}
                options={questionType}
                onChange={(selectedOption: SelectOptions) =>
                  setNewQuestionType(
                  selectedOption.value as
                    | 'numeric'
                    | 'multiple-choice'
                    | 'video',
                  )
                }
                disabled={editIndex !== null}
            />
          </div>
        </div>
        { /* Choices Section for New Question */ }
        <div className='flex flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:gap-2'>
          { newQuestionType === 'multiple-choice' && (
            <div className='flex flex-col gap-3 mt-2 sm:flex-1'>
              <label className='text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-0.5'>
                { t(AA.options) }
              </label>
              <div className='flex flex-wrap gap-2'>
                { choices.map((choice, index) => (
                  <div key={index} className='flex w-full items-center gap-2 sm:w-auto'>
                    <TextInput
                        name='choice'
                        value={choice}
                        type='text'
                        placeholder={`Option ${index + 1}`}
                        className='w-full sm:w-48'
                        onChange={(e) =>
                          handleChoiceChange(index, e.target.value, false)
                        }
                    />
                    { choices.length > 1 && (
                      <button
                          onClick={() =>
                            setChoices(choices.filter((_, i) => i !== index))
                          }
                          className='p-2 text-slate-400 hover:text-red-500 transition-colors'
                          title='Remove Option'
                      >
                        <TrashIcon className='w-4 h-4' />
                      </button>
                    ) }
                  </div>
                )) }
                <Button
                    onClick={() => setChoices([...choices, ''])}
                    variant='secondary'
                    label={t(AA.addOption)}
                    className='h-9 !py-0'
                />
              </div>
            </div>
          ) }
          <div className='flex justify-end pt-1'>
            <Button
                onClick={() => handleAddQuestion()}
                variant='primary'
                label={t(AA.addToAssessment)}
                disabled={
                  newQuestionText.trim() === '' ||
                (newQuestionType === 'multiple-choice' &&
                  choices.every((choice) => choice.trim() === '')) ||
                editIndex !== null
                }
                className='w-full shadow-md shadow-emerald-500/10 !h-10 text-sm sm:w-auto'
            />
          </div>
        </div>
      </div>
      { /* ─── Questions List Section ─── */ }
      { questions.length > 0 && (
        <div className='flex flex-col gap-5 mt-2'>
          <div className='flex items-center justify-between px-1'>
            <div className='flex items-center gap-2'>
              <div className='w-1 h-5 bg-slate-300 rounded-full' />
              <h3 className='text-[13px] font-bold text-slate-800 uppercase tracking-tight'>
                { t(AA.activeQuestions) }
              </h3>
            </div>
            <span className='px-2 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-bold rounded-md uppercase tracking-wider border border-slate-200'>
              { questions.length } { t(AA.total) }
            </span>
          </div>

          <div className='flex flex-col gap-3'>
            { questions.map((question, indexNum) => (
              <div
                  key={question.questionId}
                  className={`group flex flex-col gap-4 bg-white p-3 sm:p-4 rounded-xl border transition-all duration-300
                  ${
                    question.questionId === editIndex
                      ? 'border-emerald-400 ring-4 ring-emerald-500/5 shadow-lg shadow-emerald-500/5'
                      : 'border-slate-100 hover:border-emerald-200 hover:shadow-md hover:shadow-slate-200/30'
                  }`}
              >
                { question.questionId === editIndex ? (
                  <div className='flex flex-col gap-6'>
                    <div className='grid w-full grid-cols-1 md:grid-cols-12 items-start gap-6'>
                      <div className='md:col-span-8'>
                        <TextInput
                            label={`Question ${indexNum + 1}`}
                            name='question'
                            value={editQuestionText}
                            type='text'
                            onChange={(e) => setEditQuestionText(e.target.value)}
                        />
                      </div>
                      <div className='md:col-span-4'>
                        <CustomSelect
                            label={t(AA.type)}
                            name='questionType'
                            value={questionType.find(
                              (option) => option.value === editQuestionType,
                            )}
                            options={questionType}
                            onChange={(selectedOption: SelectOptions) =>
                              setEditQuestionType(
                              selectedOption.value as
                                | 'numeric'
                                | 'multiple-choice'
                                | 'video',
                              )
                            }
                        />
                      </div>
                    </div>

                    { editQuestionType === 'multiple-choice' && (
                      <div className='flex flex-col gap-3 p-5 bg-slate-50 rounded-xl border border-slate-200/60'>
                        <label className='text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-0.5'>
                          { t(AA.options) }
                        </label>
                        <div className='flex flex-wrap gap-2'>
                          { editChoices.map((choice, index) => (
                            <div
                                key={index}
                                className='flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200 shadow-sm'
                            >
                              <TextInput
                                  name='choice'
                                  value={choice}
                                  type='text'
                                  className='w-48 !h-8 !border-none !ring-0'
                                  onChange={(e) =>
                                    handleChoiceChange(
                                      index,
                                      e.target.value,
                                      true,
                                    )
                                  }
                              />
                              { editChoices.length > 1 && (
                                <button
                                    onClick={() =>
                                      setEditChoices(
                                        editChoices.filter((_, i) => i !== index),
                                      )
                                    }
                                    className='p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-all'
                                >
                                  <TrashIcon className='w-3.5 h-3.5' />
                                </button>
                              ) }
                            </div>
                          )) }
                          <Button
                              onClick={() => setEditChoices([...editChoices, ''])}
                              variant='secondary'
                              label={t(AA.addOption)}
                              className='h-9 !py-0 border-dashed border-2 hover:border-emerald-400 hover:text-emerald-500'
                          />
                        </div>
                      </div>
                    ) }

                    <div className='flex justify-end gap-3 pt-2'>
                      <Button
                          onClick={() => setEditIndex(null)}
                          variant='secondary'
                          label={t(AA.cancel)}
                      />
                      <Button
                          onClick={() => handleSaveQuestion()}
                          variant='primary'
                          label={t(AA.saveChanges)}
                          className='shadow-md shadow-emerald-500/10'
                      />
                    </div>
                  </div>
                ) : (
                  <div className='flex items-start gap-3 sm:items-center sm:gap-4'>
                    <div className='flex flex-col items-center justify-center w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl bg-slate-50 text-slate-400 font-bold text-sm border border-slate-100 group-hover:bg-emerald-50 group-hover:text-emerald-600 group-hover:border-emerald-200 transition-all duration-300'>
                      { indexNum + 1 }
                    </div>

                    <div className='flex-1 flex flex-col gap-2 sm:gap-3 min-w-0'>
                      <div className='flex items-center flex-wrap gap-3'>
                        <h4 className='text-sm sm:text-[15px] font-bold text-slate-800 leading-tight group-hover:text-emerald-700 transition-colors'>
                          { question.question }
                        </h4>
                        <span
                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shrink-0
                          ${
                            question.questionType === 'numeric'
                              ? 'bg-blue-50 text-blue-600 border-blue-100'
                              : question.questionType === 'video'
                                ? 'bg-purple-50 text-purple-600 border-purple-100'
                                : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                          }`}
                        >
                          {
                            questionType.find(
                              (t) => t.value === question.questionType,
                            )?.label
                          }
                        </span>
                      </div>

                      { question.questionType === 'multiple-choice' &&
                        question.choices && (
                        <div className='flex flex-wrap gap-1.5 mt-0.5'>
                          { question.choices.map((choice, idx) => (
                            <span
                                key={idx}
                                className='px-3 py-1 bg-slate-50 text-slate-500 text-[11px] font-medium rounded-full border border-slate-100 group-hover:bg-white group-hover:border-emerald-100 transition-colors'
                            >
                              { choice }
                            </span>
                          )) }
                        </div>
                      ) }
                    </div>

                    <div className='flex shrink-0 flex-col items-center gap-1 sm:flex-row'>
                      <button
                          type='button'
                          className='p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all sm:p-2.5'
                          onClick={() => handleEditQuestion(question)}
                          title='Edit'
                      >
                        <PencilIcon className='w-5 h-5' />
                      </button>
                      <button
                          className='p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all sm:p-2.5'
                          onClick={() =>
                            handleDeleteQuestion(question.questionId)
                          }
                          title='Delete'
                      >
                        <TrashIcon className='w-5 h-5' />
                      </button>
                    </div>
                  </div>
                ) }
              </div>
            )) }
          </div>
        </div>
      ) }
    </div>
  );
};
